import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useI18n } from "../i18n/I18nProvider";
import { useAuth } from "../lib/AuthProvider";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function AuthDialog({ open, onOpenChange }) {
  const { t } = useI18n();
  const { loginEmail, registerEmail } = useAuth();
  const [mode, setMode] = useState("signin");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signin") await loginEmail(form.email, form.password);
      else await registerEmail(form.name, form.email, form.password);
      toast.success("Welcome!");
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t.auth.err);
    } finally { setBusy(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[#0a0a0a] border border-white/10 rounded-none text-white" data-testid="auth-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-black text-2xl tracking-tighter">{mode === "signin" ? t.auth.signin : t.auth.signup}</DialogTitle>
        </DialogHeader>
        <div className="flex gap-1 border border-white/10 p-1 mb-2">
          {["signin", "signup"].map(m => (
            <button key={m} onClick={() => setMode(m)} className={`flex-1 h-10 text-xs font-bold tracking-[0.2em] uppercase transition-colors ${mode === m ? "bg-scooter-yellow text-black" : "text-zinc-400 hover:text-white"}`} data-testid={`tab-${m}`}>
              {m === "signin" ? t.auth.signin : t.auth.signup}
            </button>
          ))}
        </div>

        <button onClick={google} className="h-12 border border-white/20 hover:bg-white hover:text-black transition-colors text-sm font-bold flex items-center justify-center gap-2" data-testid="google-login-btn">
          <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          {t.auth.google}
        </button>

        <div className="flex items-center gap-3 my-2">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-xs uppercase tracking-[0.3em] text-zinc-500">{t.auth.or}</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === "signup" && (
            <div>
              <Label className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">{t.auth.name}</Label>
              <Input required value={form.name} onChange={(e) => setForm(f => ({...f, name: e.target.value}))} className="mt-2 bg-transparent border-white/20 rounded-none text-white focus-visible:ring-scooter-yellow" data-testid="auth-name" />
            </div>
          )}
          <div>
            <Label className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">{t.auth.email}</Label>
            <Input type="email" required value={form.email} onChange={(e) => setForm(f => ({...f, email: e.target.value}))} className="mt-2 bg-transparent border-white/20 rounded-none text-white focus-visible:ring-scooter-yellow" data-testid="auth-email" />
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">{t.auth.password}</Label>
            <Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm(f => ({...f, password: e.target.value}))} className="mt-2 bg-transparent border-white/20 rounded-none text-white focus-visible:ring-scooter-yellow" data-testid="auth-password" />
          </div>
          <button type="submit" disabled={busy} className="w-full h-12 bg-scooter-yellow text-black font-bold text-xs tracking-[0.2em] uppercase hover:bg-scooter-yellow-hover disabled:opacity-50 flex items-center justify-center gap-2" data-testid="auth-submit">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {mode === "signin" ? t.auth.sin : t.auth.sup}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
